import { useEffect, useMemo, useRef, useState } from "react"

import { BACKGROUND_NOISE_TYPES } from "@/components/shared/evaluation/EvaluationConstants"
import type { BackgroundSoundResource } from "@/types_api/backend/transport/list_background_sounds_response"
import type { PersonaMetadata } from "@/types/api/Persona"

import { useBackgroundSounds } from "./useBackgroundSounds"

const BACKGROUND_SOUND_OFF_VALUE = "off"
const BACKGROUND_SOUND_SOURCE_BUILT_IN = "built_in"
const BACKGROUND_SOUND_SOURCE_CUSTOM = "custom"
const BACKGROUND_SOUND_STATUS_ACTIVE = "active"
const BACKGROUND_SOUND_STATUS_ARCHIVED = "archived"
const DEFAULT_FALLBACK_BACKGROUND_SOUND_VOLUME = 0.3

interface UsePersonaBackgroundSoundActionsOptions {
  isOpen: boolean
  isInSilentMode: boolean
  metadata: PersonaMetadata | undefined
  includeCustomSounds?: boolean
}

export function usePersonaBackgroundSoundActions({
  isOpen,
  isInSilentMode,
  metadata,
  includeCustomSounds = true,
}: UsePersonaBackgroundSoundActionsOptions) {
  const [customSoundNameDraft, setCustomSoundNameDraft] = useState("")
  const {
    backgroundSounds,
    uploadBackgroundSound,
    updateBackgroundSound,
    isUploading,
    isUpdating,
  } = useBackgroundSounds({
    includeArchived: true,
    enabled: isOpen && !isInSilentMode,
  })

  const fallbackBackgroundSounds = useMemo<BackgroundSoundResource[]>(
    () =>
      BACKGROUND_NOISE_TYPES.map((sound) => ({
        id: sound.id,
        value: sound.id,
        source: BACKGROUND_SOUND_SOURCE_BUILT_IN,
        display_name: sound.name,
        status: BACKGROUND_SOUND_STATUS_ACTIVE,
        preview_url: sound.previewUrl,
        default_volume:
          sound.defaultVolume ?? DEFAULT_FALLBACK_BACKGROUND_SOUND_VOLUME,
      })),
    [],
  )

  const backgroundSoundCatalog = useMemo(() => {
    // Build a set of `value` identifiers that the backend already provides.
    const backendValueSet = new Set(
      backgroundSounds.map((sound) => sound.value),
    )

    // Append any fallback built-in sounds whose `value` is not in the
    // backend response. Backend-provided resources remain the source of
    // truth for ids they cover.
    const missingFallbackSounds = fallbackBackgroundSounds.filter(
      (sound) => !backendValueSet.has(sound.value),
    )

    const availableBackgroundSounds = includeCustomSounds
      ? backgroundSounds
      : backgroundSounds.filter(
          (sound) => sound.source === BACKGROUND_SOUND_SOURCE_BUILT_IN,
        )

    return [...availableBackgroundSounds, ...missingFallbackSounds]
  }, [backgroundSounds, fallbackBackgroundSounds, includeCustomSounds])

  const selectedBackgroundSoundValue =
    metadata?.background_sound ?? BACKGROUND_SOUND_OFF_VALUE

  const {
    selectedBackgroundSound,
    selectedCustomBackgroundSound,
    activeBuiltInBackgroundSounds,
    activeCustomBackgroundSounds,
    selectedArchivedBackgroundSound,
  } = useMemo(() => {
    const selectedBackgroundSoundByValue = backgroundSoundCatalog.find(
      (sound) => sound.value === selectedBackgroundSoundValue,
    )
    const selectedBackgroundSound = (() => {
      if (selectedBackgroundSoundByValue) {
        return selectedBackgroundSoundByValue
      }

      if (selectedBackgroundSoundValue.startsWith("custom:")) {
        const customSoundId = selectedBackgroundSoundValue.slice(
          "custom:".length,
        )

        return backgroundSoundCatalog.find(
          (sound) =>
            sound.source === BACKGROUND_SOUND_SOURCE_CUSTOM &&
            sound.id === customSoundId,
        )
      }

      return backgroundSoundCatalog.find(
        (sound) =>
          sound.source === BACKGROUND_SOUND_SOURCE_CUSTOM &&
          sound.id === selectedBackgroundSoundValue,
      )
    })()

    const selectedCustomBackgroundSound =
      selectedBackgroundSound?.source === BACKGROUND_SOUND_SOURCE_CUSTOM
        ? selectedBackgroundSound
        : undefined

    return {
      selectedBackgroundSound,
      selectedCustomBackgroundSound,
      activeBuiltInBackgroundSounds: backgroundSoundCatalog.filter(
        (sound) =>
          sound.source === BACKGROUND_SOUND_SOURCE_BUILT_IN &&
          sound.status === BACKGROUND_SOUND_STATUS_ACTIVE,
      ),
      activeCustomBackgroundSounds: backgroundSoundCatalog.filter(
        (sound) =>
          sound.source === BACKGROUND_SOUND_SOURCE_CUSTOM &&
          sound.status === BACKGROUND_SOUND_STATUS_ACTIVE,
      ),
      selectedArchivedBackgroundSound:
        selectedCustomBackgroundSound?.status ===
        BACKGROUND_SOUND_STATUS_ARCHIVED
          ? selectedCustomBackgroundSound
          : undefined,
    }
  }, [backgroundSoundCatalog, selectedBackgroundSoundValue])

  // Read through a ref so `display_name` seeds the draft without being a dep —
  // as a dep it would overwrite whatever the user had typed the moment the saved
  // name changed.
  const selectedCustomBackgroundSoundRef = useRef(selectedCustomBackgroundSound)
  selectedCustomBackgroundSoundRef.current = selectedCustomBackgroundSound

  // Seed the rename draft from the selected custom sound, keyed on that sound's
  // identity alone.
  //
  // Deliberately NOT keyed on the persona form's initialization: this draft
  // belongs to the sound — an org-level asset shared by every persona that uses
  // it — not to the persona being edited, so the persona form's lifecycle has no
  // business resetting it. Keying on it silently discarded in-progress renames,
  // because mutating any background sound also invalidates the `personas` cache
  // tag (see useBackgroundSoundsTransport), which refetches the persona and
  // re-initializes the form.
  useEffect(() => {
    if (!isOpen) {
      return
    }

    setCustomSoundNameDraft(
      selectedCustomBackgroundSoundRef.current?.display_name ?? "",
    )
  }, [isOpen, selectedCustomBackgroundSound?.id])

  return {
    backgroundSoundCatalog,
    selectedBackgroundSoundValue,
    selectedBackgroundSound,
    selectedCustomBackgroundSound,
    activeBuiltInBackgroundSounds,
    activeCustomBackgroundSounds,
    selectedArchivedBackgroundSound,
    customSoundNameDraft,
    setCustomSoundNameDraft,
    uploadBackgroundSound,
    updateBackgroundSound,
    isBackgroundSoundUploading: isUploading,
    isBackgroundSoundUpdating: isUpdating,
  }
}
